import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';

interface BottleAttributes {
  product_name: string;
  bottle_type: 'whisky' | 'wine' | 'sake';
  producer: string;
  country: string;
  strength: number;
  volume: number;
  opened_date?: string;
  remaining_status: 'remaining' | 'empty';
  notes?: string;
  category?: string;
  distillery?: string;
  winery?: string;
  brewery?: string;
  age?: number;
  vintage?: number;
  cask_type?: string;
  cask_number?: string;
  bottleType?: string;
  bottlingYear?: number;
  total_bottles?: number;
  market?: string;
}

export const BottleAttributesPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { bottleId } = useParams();
  const { user } = useAuthStore();
  
  const [attributes, setAttributes] = useState<BottleAttributes>({
    product_name: '',
    bottle_type: 'whisky',
    producer: '',
    country: '',
    strength: 0,
    volume: 700,
    remaining_status: 'remaining'
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [aiAnalysisResult, setAiAnalysisResult] = useState<any>(null);
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [presignedUrls, setPresignedUrls] = useState<string[]>([]);
  const [mediumPresignedUrls, setMediumPresignedUrls] = useState<string[]>([]);
  const [imageLoadingStates, setImageLoadingStates] = useState<boolean[]>([]);
  const [imageErrorStates, setImageErrorStates] = useState<boolean[]>([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);

  useEffect(() => {
    // ページ表示時に常にトップにスクロール
    window.scrollTo(0, 0);

    if (bottleId) {
      setIsEditMode(true);
      fetchBottleData();
    } else {
      const analysisResult = location.state?.analysisResult;
      if (analysisResult) {
        // AI解析結果を保存
        setAiAnalysisResult(analysisResult);
        
        // extracted_infoから属性を取得
        const extractedInfo = analysisResult.extracted_info || analysisResult;
        setAttributes(prev => ({
          ...prev,
          product_name: extractedInfo.product_name || '',
          bottle_type: extractedInfo.bottle_type || 'whisky',
          producer: extractedInfo.producer || '',
          country: extractedInfo.country || '',
          strength: extractedInfo.strength || 0,
          volume: extractedInfo.volume || 700,
          // product_attributesから酒類別属性を取得
          ...(extractedInfo.product_attributes && {
            category: extractedInfo.product_attributes.category,
            distillery: extractedInfo.product_attributes.distillery,
            winery: extractedInfo.product_attributes.winery,
            brewery: extractedInfo.product_attributes.brewery,
            age: extractedInfo.product_attributes.age,
            vintage: extractedInfo.product_attributes.vintage,
            cask_type: extractedInfo.product_attributes.cask_type,
            cask_number: extractedInfo.product_attributes.cask_number,
            bottleType: extractedInfo.product_attributes.bottleType,
            bottlingYear: extractedInfo.product_attributes.bottlingYear,
            total_bottles: extractedInfo.product_attributes.total_bottles,
            market: extractedInfo.product_attributes.market
          })
        }));
      }
    }
  }, [bottleId, location.state]);

  const fetchPresignedUrls = async (urls: string[], size: 'thumbnail' | 'medium' | 'original' = 'thumbnail') => {
    if (!user?.store_id) return;

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/stores/${user.store_id}/images/download`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('idToken')}`,
          },
          body: JSON.stringify({ image_urls: urls, size }),
        }
      );

      if (!response.ok) {
        throw new Error('Presigned URL取得に失敗しました');
      }

      const data = await response.json();
      const downloadUrls = data.data.download_urls;

      const presigned = downloadUrls.map((item: any) => item.presigned_url);
      const loadingStates = downloadUrls.map((item: any) => !item.presigned_url);
      const errorStates = downloadUrls.map((item: any) => !item.presigned_url);

      setPresignedUrls(presigned);
      setImageLoadingStates(loadingStates);
      setImageErrorStates(errorStates);
    } catch (err) {
      console.error('Presigned URL fetch error:', err);
      setImageErrorStates(new Array(urls.length).fill(true));
      setImageLoadingStates(new Array(urls.length).fill(false));
    }
  };

  const fetchMediumPresignedUrls = async (urls: string[]) => {
    if (!user?.store_id) return;

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/stores/${user.store_id}/images/download`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('idToken')}`,
          },
          body: JSON.stringify({ image_urls: urls, size: 'medium' }),
        }
      );

      if (!response.ok) {
        throw new Error('Medium Presigned URL取得に失敗しました');
      }

      const data = await response.json();
      const downloadUrls = data.data.download_urls;
      const presigned = downloadUrls.map((item: any) => item.presigned_url);
      setMediumPresignedUrls(presigned);
    } catch (err) {
      console.error('Medium Presigned URL fetch error:', err);
    }
  };

  const fetchBottleData = async () => {
    if (!user?.store_id || !bottleId) return;

    setIsLoadingData(true);
    setError(null);

    try {
      // Decode the bottle ID from URL parameters
      const decodedBottleId = decodeURIComponent(bottleId);
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/stores/${user.store_id}/bottles/${encodeURIComponent(decodedBottleId)}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('idToken')}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('ボトル情報の取得に失敗しました');
      }

      const data = await response.json();
      
      // APIレスポンスを新しいフィールド名にマッピング
      const mappedData = {
        product_name: data.data.product_name || data.data.name,
        bottle_type: data.data.bottle_type || data.data.type,
        remaining_status: data.data.remaining_status || data.data.status,
        producer: data.data.producer,
        country: data.data.country,
        strength: data.data.strength,
        volume: data.data.volume,
        opened_date: data.data.opened_date,
        notes: data.data.notes,
        category: data.data.category,
        distillery: data.data.distillery,
        winery: data.data.winery,
        brewery: data.data.brewery,
        age: data.data.age,
        vintage: data.data.vintage,
        cask_type: data.data.cask_type,
        cask_number: data.data.cask_number,
        bottleType: data.data.bottleType,
        bottlingYear: data.data.bottlingYear,
        total_bottles: data.data.total_bottles,
        market: data.data.market
      };
      setAttributes(mappedData);

      // ai_analysisとproduct_attributesを保存
      if (data.data.ai_analysis) {
        try {
          const aiAnalysis = typeof data.data.ai_analysis === 'string' 
            ? JSON.parse(data.data.ai_analysis) 
            : data.data.ai_analysis;
          setAiAnalysisResult(aiAnalysis);
        } catch (e) {
          console.error('Failed to parse ai_analysis:', e);
        }
      } else if (data.data.product_attributes) {
        // ai_analysisがない場合、product_attributesから構築
        try {
          const productAttrs = typeof data.data.product_attributes === 'string'
            ? JSON.parse(data.data.product_attributes)
            : data.data.product_attributes;
          setAiAnalysisResult({ product_attributes: productAttrs });
        } catch (e) {
          console.error('Failed to parse product_attributes:', e);
        }
      }

      // 画像URLを取得してPresigned URL生成
      if (data.data.image_urls && Array.isArray(data.data.image_urls) && data.data.image_urls.length > 0) {
        setImageUrls(data.data.image_urls);
        setImageLoadingStates(new Array(data.data.image_urls.length).fill(true));
        setImageErrorStates(new Array(data.data.image_urls.length).fill(false));
        await fetchPresignedUrls(data.data.image_urls, 'thumbnail');
        await fetchMediumPresignedUrls(data.data.image_urls);
      }
    } catch (err: any) {
      console.error('Fetch error:', err);
      setError(err.message || 'データの取得中にエラーが発生しました');
    } finally {
      setIsLoadingData(false);
    }
  };

  const handleInputChange = (field: keyof BottleAttributes, value: any) => {
    setAttributes(prev => ({
      ...prev,
      [field]: value
    }));
    setError(null);
  };

  const validateForm = (): boolean => {
    if (!attributes.product_name.trim()) {
      setError('商品名を入力してください');
      return false;
    }
    if (!attributes.producer.trim()) {
      setError('製造者を入力してください');
      return false;
    }
    if (!attributes.country.trim()) {
      setError('生産国を入力してください');
      return false;
    }
    if (attributes.strength <= 0 || attributes.strength > 100) {
      setError('アルコール度数は1-100の範囲で入力してください');
      return false;
    }
    if (attributes.volume <= 0) {
      setError('容量を正しく入力してください');
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    if (!user?.store_id) {
      setError('店舗情報が取得できません');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const url = isEditMode 
        ? `${import.meta.env.VITE_API_BASE_URL}/stores/${user.store_id}/bottles/${encodeURIComponent(decodeURIComponent(bottleId!))}`
        : `${import.meta.env.VITE_API_BASE_URL}/stores/${user.store_id}/bottles`;
      
      const method = isEditMode ? 'PUT' : 'POST';

      // remaining_statusをそのまま送信（Backend側で対応済み）
      const requestData = {
        ...attributes,
        // AI解析結果がある場合は含める
        ...(aiAnalysisResult && !isEditMode && {
          ai_analysis: aiAnalysisResult
        })
      };

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('idToken')}`,
        },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.message || `ボトル情報の保存に失敗しました (HTTP ${response.status})`;
        console.error('API Error:', { status: response.status, data: errorData });
        throw new Error(errorMessage);
      }

      navigate('/bottles');
    } catch (err: any) {
      console.error('Save error:', err);
      setError(err.message || '保存中にエラーが発生しました');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!user?.store_id) {
      setError('店舗情報が取得できません');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const copyData = {
        product_name: attributes.product_name,
        bottle_type: attributes.bottle_type,
        producer: attributes.producer,
        country: attributes.country,
        strength: attributes.strength,
        volume: attributes.volume,
        remaining_status: 'remaining' as const,
        notes: attributes.notes,
        category: attributes.category,
        distillery: attributes.distillery,
        winery: attributes.winery,
        brewery: attributes.brewery,
        age: attributes.age,
        vintage: attributes.vintage,
        cask_type: attributes.cask_type,
        cask_number: attributes.cask_number,
        bottleType: attributes.bottleType,
        bottlingYear: attributes.bottlingYear,
        total_bottles: attributes.total_bottles,
        market: attributes.market,
        image_urls: imageUrls,
        skip_analysis: true,
      };

      // product_attributesが存在する場合は追加
      if (aiAnalysisResult?.product_attributes) {
        (copyData as any).product_attributes = aiAnalysisResult.product_attributes;
      }

      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/stores/${user.store_id}/bottles`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('idToken')}`,
          },
          body: JSON.stringify(copyData),
        }
      );

      if (!response.ok) {
        throw new Error('ボトルのコピーに失敗しました');
      }

      const result = await response.json();
      navigate(`/bottles/${encodeURIComponent(result.data.bottle_id)}/attributes`);
    } catch (err: any) {
      console.error('Copy error:', err);
      setError(err.message || 'コピー中にエラーが発生しました');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!user?.store_id || !bottleId) return;

    setIsLoading(true);
    setError(null);

    try {
      const decodedBottleId = decodeURIComponent(bottleId!);
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/stores/${user.store_id}/bottles/${encodeURIComponent(decodedBottleId)}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('idToken')}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('ボトルの削除に失敗しました');
      }

      navigate('/bottles');
    } catch (err: any) {
      console.error('Delete error:', err);
      setError(err.message || '削除中にエラーが発生しました');
    } finally {
      setIsLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  const renderTypeSpecificFields = () => {
    switch (attributes.bottle_type) {
      case 'whisky':
        return (
          <>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                カテゴリー
              </label>
              <select
                value={attributes.category || ''}
                onChange={(e) => handleInputChange('category', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">選択してください</option>
                <option value="Single Malt">シングルモルト</option>
                <option value="Blended">ブレンデッド</option>
                <option value="Bourbon">バーボン</option>
                <option value="Japanese">ジャパニーズ</option>
              </select>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                蒸留所
              </label>
              <input
                type="text"
                value={attributes.distillery || ''}
                onChange={(e) => handleInputChange('distillery', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="蒸留所名"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                熟成年数
              </label>
              <input
                type="number"
                value={attributes.age || ''}
                onChange={(e) => handleInputChange('age', parseInt(e.target.value) || undefined)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="年"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ヴィンテージ（蒸留年）
              </label>
              <input
                type="number"
                value={attributes.vintage || ''}
                onChange={(e) => handleInputChange('vintage', parseInt(e.target.value) || undefined)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="年"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                カスクタイプ
              </label>
              <input
                type="text"
                value={attributes.cask_type || ''}
                onChange={(e) => handleInputChange('cask_type', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Oloroso, Ex-Bourbon等"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                カスク番号
              </label>
              <input
                type="text"
                value={attributes.cask_number || ''}
                onChange={(e) => handleInputChange('cask_number', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="カスク番号"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ボトルタイプ
              </label>
              <input
                type="text"
                value={attributes.bottleType || ''}
                onChange={(e) => handleInputChange('bottleType', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Single Cask, Cask Strength等"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ボトリング年
              </label>
              <input
                type="number"
                value={attributes.bottlingYear || ''}
                onChange={(e) => handleInputChange('bottlingYear', parseInt(e.target.value) || undefined)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="年"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ボトリング本数
              </label>
              <input
                type="number"
                value={attributes.total_bottles || ''}
                onChange={(e) => handleInputChange('total_bottles', parseInt(e.target.value) || undefined)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="本数"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                販売先市場
              </label>
              <input
                type="text"
                value={attributes.market || ''}
                onChange={(e) => handleInputChange('market', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Distillery Exclusive等"
              />
            </div>
          </>
        );
      case 'wine':
        return (
          <>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                カテゴリー
              </label>
              <select
                value={attributes.category || ''}
                onChange={(e) => handleInputChange('category', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">選択してください</option>
                <option value="Red">赤ワイン</option>
                <option value="White">白ワイン</option>
                <option value="Rosé">ロゼ</option>
                <option value="Sparkling">スパークリング</option>
              </select>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ワイナリー
              </label>
              <input
                type="text"
                value={attributes.winery || ''}
                onChange={(e) => handleInputChange('winery', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="ワイナリー名"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ヴィンテージ
              </label>
              <input
                type="number"
                value={attributes.vintage || ''}
                onChange={(e) => handleInputChange('vintage', parseInt(e.target.value) || undefined)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="年"
              />
            </div>
          </>
        );
      case 'sake':
        return (
          <>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                カテゴリー
              </label>
              <select
                value={attributes.category || ''}
                onChange={(e) => handleInputChange('category', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">選択してください</option>
                <option value="純米大吟醸">純米大吟醸</option>
                <option value="大吟醸">大吟醸</option>
                <option value="純米吟醸">純米吟醸</option>
                <option value="吟醸">吟醸</option>
                <option value="純米">純米</option>
              </select>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                蔵元
              </label>
              <input
                type="text"
                value={attributes.brewery || ''}
                onChange={(e) => handleInputChange('brewery', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="蔵元名"
              />
            </div>
          </>
        );
      default:
        return null;
    }
  };

  if (isLoadingData) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-md mx-auto">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="text-center text-gray-600 mt-8">
              <p>読み込み中...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-md mx-auto">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold text-gray-900">
              {isEditMode ? 'ボトル編集' : 'ボトル情報'}
            </h1>
            <button
              onClick={() => navigate('/bottles')}
              className="text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          </div>

          {/* 画像表示セクション */}
          {imageUrls.length > 0 && (
            <div className="mb-6">
              <h2 className="text-lg font-semibold mb-4">📷 アップロード写真</h2>
              <div className="flex gap-2">
                {imageUrls.map((_, index) => (
                  <div
                    key={index}
                    className="w-24 h-24 border border-gray-200 rounded-lg bg-gray-100 flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => !imageErrorStates[index] && setSelectedImageIndex(index)}
                  >
                    {imageLoadingStates[index] ? (
                      <div className="text-gray-400">⟳</div>
                    ) : imageErrorStates[index] ? (
                      <div className="text-center text-xs text-gray-500">
                        <div>⚠</div>
                        <div>読込失敗</div>
                      </div>
                    ) : (
                      <img
                        src={presignedUrls[index]}
                        alt={`ボトル画像 ${index + 1}`}
                        className="w-full h-full object-cover rounded-lg"
                        onLoad={() => {
                          const newStates = [...imageLoadingStates];
                          newStates[index] = false;
                          setImageLoadingStates(newStates);
                        }}
                        onError={() => {
                          const newErrorStates = [...imageErrorStates];
                          newErrorStates[index] = true;
                          setImageErrorStates(newErrorStates);
                          const newLoadingStates = [...imageLoadingStates];
                          newLoadingStates[index] = false;
                          setImageLoadingStates(newLoadingStates);
                        }}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 基本情報 */}
          <div className="mb-6">
            <h2 className="text-lg font-semibold mb-4">基本情報</h2>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                商品名 *
              </label>
              <input
                type="text"
                value={attributes.product_name}
                onChange={(e) => handleInputChange('product_name', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="商品名"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                酒類タイプ *
              </label>
              <select
                value={attributes.bottle_type}
                onChange={(e) => handleInputChange('bottle_type', e.target.value as 'whisky' | 'wine' | 'sake')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="whisky">ウィスキー</option>
                <option value="wine">ワイン</option>
                <option value="sake">日本酒</option>
              </select>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                製造者 *
              </label>
              <input
                type="text"
                value={attributes.producer}
                onChange={(e) => handleInputChange('producer', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="製造者"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                生産国 *
              </label>
              <input
                type="text"
                value={attributes.country}
                onChange={(e) => handleInputChange('country', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="生産国"
              />
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  アルコール度数 * (%)
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={attributes.strength}
                  onChange={(e) => handleInputChange('strength', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="40.0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  容量 * (ml)
                </label>
                <input
                  type="number"
                  value={attributes.volume}
                  onChange={(e) => handleInputChange('volume', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="700"
                />
              </div>
            </div>
          </div>

          {/* 酒類別詳細情報 */}
          <div className="mb-6">
            <h2 className="text-lg font-semibold mb-4">詳細情報</h2>
            {renderTypeSpecificFields()}
          </div>

          {/* 店舗管理情報 */}
          <div className="mb-6">
            <h2 className="text-lg font-semibold mb-4">店舗管理情報</h2>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                開栓日
              </label>
              <input
                type="date"
                value={attributes.opened_date || ''}
                onChange={(e) => handleInputChange('opened_date', e.target.value || undefined)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                残量状況
              </label>
              <div className="flex flex-col space-y-2">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="remaining_status"
                    value="remaining"
                    checked={attributes.remaining_status === 'remaining'}
                    onChange={(e) => handleInputChange('remaining_status', e.target.value)}
                    className="mr-2"
                  />
                  有り
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="remaining_status"
                    value="empty"
                    checked={attributes.remaining_status === 'empty'}
                    onChange={(e) => handleInputChange('remaining_status', e.target.value)}
                    className="mr-2"
                  />
                  空
                </label>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                店舗メモ
              </label>
              <textarea
                value={attributes.notes || ''}
                onChange={(e) => handleInputChange('notes', e.target.value || undefined)}
                placeholder="このボトルに関するメモを入力してください"
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="mt-1 text-sm text-gray-500">
                お客様への説明、仕入れ情報、特記事項など
              </p>
            </div>
          </div>

          {/* エラーメッセージ */}
          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          {/* 保存ボタン */}
          <button
            onClick={handleSave}
            disabled={isLoading}
            className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed mb-4"
          >
            {isLoading ? '保存中...' : isEditMode ? 'ボトル情報を更新' : 'ボトル情報を保存'}
          </button>

          {/* コピーボタン（編集モードのみ） */}
          {isEditMode && (
            <button
              onClick={handleCopy}
              disabled={isLoading}
              className="w-full bg-green-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed mb-4"
            >
              {isLoading ? 'コピー中...' : '📋 コピーして新規作成'}
            </button>
          )}

          {/* 削除ボタン（編集モードのみ） */}
          {isEditMode && (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              disabled={isLoading}
              className="w-full bg-red-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              ボトルを削除
            </button>
          )}

          {/* 削除確認ダイアログ */}
          {showDeleteConfirm && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 max-w-sm mx-4">
                <h3 className="text-lg font-semibold mb-4">削除確認</h3>
                <p className="text-gray-600 mb-6">
                  このボトルを削除しますか？この操作は取り消せません。
                </p>
                <div className="flex space-x-4">
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    キャンセル
                  </button>
                  <button
                    onClick={handleDelete}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                  >
                    削除
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* 画像拡大モーダル */}
          {selectedImageIndex !== null && (
            <div 
              className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50"
              onClick={() => setSelectedImageIndex(null)}
            >
              <button
                onClick={() => setSelectedImageIndex(null)}
                className="absolute top-4 right-4 text-white text-3xl hover:text-gray-300"
              >
                ✕
              </button>
              <div className="relative max-w-[90%] max-h-[80vh]">
                <img
                  src={mediumPresignedUrls[selectedImageIndex] || presignedUrls[selectedImageIndex]}
                  alt={`ボトル画像 ${selectedImageIndex + 1}`}
                  className="max-w-full max-h-[80vh] object-contain"
                  onClick={(e) => e.stopPropagation()}
                />
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white bg-black bg-opacity-50 px-4 py-2 rounded-lg flex items-center gap-4">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedImageIndex(Math.max(0, selectedImageIndex - 1));
                    }}
                    disabled={selectedImageIndex === 0}
                    className="disabled:opacity-30"
                  >
                    ←
                  </button>
                  <span>{selectedImageIndex + 1} / {imageUrls.length}</span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedImageIndex(Math.min(imageUrls.length - 1, selectedImageIndex + 1));
                    }}
                    disabled={selectedImageIndex === imageUrls.length - 1}
                    className="disabled:opacity-30"
                  >
                    →
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
